import React from 'react';
import _ from 'lodash';

import {
  fields,
  formUtil
} from 'iron-forge-react-modules';
import {
  Controller,
  useFieldArray,
  useForm
} from 'react-hook-form';
import {
  Grid,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import {
  FormModal,
  appFormUtil
} from 'modules/layer00';

import { ReturnAuthorizationSummarySection } from 'modules/layer01';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();
const ControlledCheckBox = fields.CheckboxField.genControllerComponent<types.IFormState>();

export interface IProps {
  returnAuthorizationId: string;
  storeId?: string;
  onClose: () => void;
  onReceiveItems?: () => void;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.returnAuthorizationId);
  const [
    receiveReturnAuthorization,
    receiveReturnAuthorizationTuple
  ] = dataService.useReceiveReturnAuthorization(props.returnAuthorizationId, props.storeId);
  const [
    saveItemReceiptMutation,
    itemReceiptMutationTuple
  ] = dataService.useSaveItemReceipt(queryTuple.data.itemReceiptId);

  const form = useForm<types.IFormState>({
    defaultValues: {
      lineItems: []
    }
  });

  const linesFieldArray = useFieldArray<types.IFormState>({
    control: form.control,
    name: 'lineItems'
  });

  formUtil.useSetFormFields(queryTuple, form.setValue, ['lineItems']);



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      const { lineItems } = appFormUtil.trimFormStateValues(formState);
      const selectedLineItems = _.filter(lineItems, li => li.isReceived);
      if (_.isEmpty(selectedLineItems)) {
        window.alert("Please acknowledge the receipt of at least one item.");
        return;
      }

      const isConfirmed = window.confirm(
        `I confirm that ${selectedLineItems.length} item(s) have been received.`
      );

      if (isConfirmed) {
        if (queryTuple.data.itemReceiptId) {
          await saveItemReceiptMutation({ lineItems });
        } else {
          await receiveReturnAuthorization(selectedLineItems);
        }

        if (props.onReceiveItems) {
          props.onReceiveItems();
        }
        props.onClose();
      }
    },
    [
      props,
      queryTuple.data.itemReceiptId,
      saveItemReceiptMutation,
      receiveReturnAuthorization
    ]
  );



  const options = React.useMemo(
    () => queryTuple.data.lineItems,
    [queryTuple.data.lineItems]
  );

  const loading = queryTuple.loading || receiveReturnAuthorizationTuple.loading || itemReceiptMutationTuple.loading;

  return (
    <FormModal.FC
      title="Receive Items"
      isLoading={loading}
      onClose={props.onClose}
      onSubmit={form.handleSubmit(processSubmit)}
      isDoubleWide>
      <Grid
        container
        spacing={1}
        rowSpacing={2}>
        <Grid
          item
          xs={12}>
          <ReturnAuthorizationSummarySection.FC
            returnAuthorizationId={props.returnAuthorizationId}
            fieldsToHide={['orderDate']}
            hideLineItems />
        </Grid>

        {linesFieldArray.fields.map((line, index) => (
          <React.Fragment key={index}>
            <Grid
              item
              xs={5}>
              <Controller
                name={`lineItems.${index}.returnAuthorizationLineItemId`}
                control={form.control}
                render={({ field }) => (
                  <Select
                    {...field}
                    disabled
                    fullWidth
                    size="small">
                    {_.map(options, opt => (
                      <MenuItem
                        key={opt.id}
                        value={opt.id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                )} />

              <Typography variant="caption">
                {line.productName}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <ControlledTextField
                isDisabled={loading}
                disableDefaultMargins
                label="IMEI/ICCID"
                size="small"
                fieldKey={`lineItems.${index}.serialNumber`}
                formControl={form.control} />

              <Typography variant="caption">
                Expected: {line.serialNumber}
              </Typography>
            </Grid>

            <Grid
              item
              xs={2}>
              <ControlledCheckBox
                isDisabled={loading}
                fieldKey={`lineItems.${index}.isReceived`}
                label="In box?"
                formControl={form.control} />
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </FormModal.FC>
  );
};
